import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import MuiLoader from "../../components/MuiLoader";
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Box,
  Typography,
  Container,
  createTheme,
  ThemeProvider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import avatar from "./../../assets/images/logo.png";
import { authenticateUserThunk } from "./Login.Thunk";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { setEncryptedItem, getDecryptedItem } from "../../utils/storageUtils";
import { v4 as uuidv4 } from "uuid";

const theme = createTheme();

function LoginPage() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [toastShown, setToastShown] = useState(false);
  const [loginAttempted, setLoginAttempted] = useState(false);

  const loading = useSelector((state) => state.LoginSlice.loading);
  const message = useSelector((state) => state.LoginSlice.message);
  const loggedin = useSelector((state) => state.LoginSlice.loggedin);

  // Set unique ID if not present
  useEffect(() => {
    if (!getDecryptedItem("uniqueId")) {
      const newUuid = uuidv4();
      setEncryptedItem("uniqueId", newUuid);
    }
  }, []);

  // Toast function
  const showToast = useCallback(
    (msg) => {
      if (!toastShown) {
        toast.error(msg);
        setToastShown(true);
      }
    },
    [toastShown]
  );

  // Show toast only after login button click
  useEffect(() => {
    if (!loginAttempted || loading || toastShown) return;

    if (message) {
      if (loggedin) {
        toast.success(message);
      } else {
        alert("Invalid Id/ Password !");
      }
      setToastShown(true);
    }
  }, [message, loggedin, loading, loginAttempted, toastShown, showToast]);

  const login_button_click = () => {
    setToastShown(false);
    setLoginAttempted(true);

    if (!email) {
      showToast("Please enter a valid emailid");
    } else if (!password) {
      showToast("Please enter a valid password");
    } else {
      const user = {
        userid: email,
        pswd: password,
      };
      dispatch(authenticateUserThunk(user));
    }
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar
              sx={{ m: 1, width: 56, height: 56 }}
              alt="Logo"
              src={avatar}
            />
            <Typography component="h1" variant="h5">
              Sign in here!
            </Typography>
            <Box noValidate sx={{ mt: 1 }}>
              <TextField
                id="email"
                label="Email Address"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                margin="normal"
                required
                fullWidth
                autoFocus
              />
              <TextField
                id="password"
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                margin="normal"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                onClick={login_button_click}
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Sign In here
              </Button>
            </Box>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 8, mb: 4 }}
          >
            {"Copyright © "}
            <Link color="inherit" href="https://thinkzone.in/" target="_blank">
              ThinkZone Edubridge
            </Link>{" "}
            {new Date().getFullYear()}
            {"."}
          </Typography>
        </Container>
      </ThemeProvider>

      <MuiLoader open={loading} />
      <ToastContainer />
    </>
  );
}

export default LoginPage;
